import React, { useMemo } from 'react';
import ProtectedImage from './components/ProtectedImage';
import { getColorForPosition } from './utils/colorUtils';

// Memoize component for performance
const GridView = React.memo(function GridView({
  visibleCols,
  visibleRows,
  scaleFactor,
  cardCollection,
  logicalX,
  logicalY,
  centerX,
  centerY,
  currentX,
  currentY,
  onItemClick,
}) {
  // Performance: Detect heavy zoom for simplified rendering
  const isHeavyZoom = scaleFactor < 0.8;
  const cellCount = visibleCols * visibleRows;
  const isHighCellCount = cellCount > 100;
  
  const items = useMemo(() => {
    // Performance: Remove individual card scaling animations
    // Cards now use static sizing for smooth zooming

    // Using shared color utility for consistency
    
    // Expansive feel: Render extra overflow cells with low opacity
    const overflowPadding = 1; // One extra row/col on each side

    const arr = [];
    for (let row = -overflowPadding; row < visibleRows + overflowPadding; row++) {
      for (let col = -overflowPadding; col < visibleCols + overflowPadding; col++) {
        const lx = col + logicalX - centerX;
        const ly = row + logicalY - centerY;
        const selected = (col === currentX && row === currentY);
        
        // Detect overflow cells for opacity
        const isOverflow = col < 0 || col >= visibleCols || row < 0 || row >= visibleRows;
        
        // Get card at this position
        const card = cardCollection.getCardAt(lx, ly);
        
        if (card) {
          arr.push({
            col, row, lx, ly,
            card,
            hasImage: !!card.imageData,
            color: card.getColor(),
            selected,
            isOverflow,
          });
        } else {
          // Empty position with fallback color - use shared utility for consistency
          arr.push({
            col, row, lx, ly,
            card: null,
            hasImage: false,
            color: getColorForPosition(lx, ly),
            selected,
            isOverflow,
          });
        }
      }
    }
    return arr;
  }, [visibleCols, visibleRows, cardCollection, logicalX, logicalY, centerX, centerY, currentX, currentY]);

  const gridStyle = useMemo(() => {
    // Include overflow cells in grid template
    const overflowPadding = 1;
    const totalCols = visibleCols + (overflowPadding * 2);
    const totalRows = visibleRows + (overflowPadding * 2);
    
    return {
      gridTemplateColumns: `repeat(${totalCols}, var(--grid-cell-width))`,
      gridTemplateRows: `repeat(${totalRows}, var(--grid-cell-height))`,
    };
  }, [visibleCols, visibleRows]);

  return (
    <div id="grid" style={gridStyle}>
      {items.map((item) => {
        // Performance: Simplified styling when zoomed out or many cells
        const simplifiedMode = isHeavyZoom || isHighCellCount;
        const itemClasses = `grid-item${item.selected ? ' selected' : ''}${item.hasImage ? ' has-image' : ''}${simplifiedMode ? ' simplified' : ''}${item.isOverflow ? ' overflow' : ''}`;
        
        return (
        <div
          key={`${item.row}-${item.col}`}
          className={itemClasses}
          style={{
            backgroundColor: item.hasImage ? 'transparent' : item.color,
            position: 'relative',
            overflow: 'hidden',
            opacity: item.isOverflow ? 0.3 : 1, // Low opacity for peek-through effect
            pointerEvents: item.isOverflow ? 'none' : 'auto', // Disable clicks on overflow cells
          }}
          onClick={() => !item.isOverflow && onItemClick(item.col, item.row)}
        >
          {item.hasImage && item.card ? (
            <ProtectedImage
              imageData={item.card.imageData}
              className="grid-card-image"
              alt={item.card.metadata.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            // Empty position indicator for admin mode
            <div className="empty-position">
              {item.lx},{item.ly}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
});

export default GridView;
